import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IoSearchOutline,
  IoArrowBack,
  IoClose,
  IoBarcodeOutline,
  IoCubeOutline,
  IoLibraryOutline,
} from "react-icons/io5";
import api from "@/api";
import { useAuth } from "@/auth";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

/**
 * Full-catalog search page for the Conad listino (barcode_cache).
 * Same visual language as the GlobalSearchPage: sticky rounded search header
 * with back button, and alternating red/yellow gradient outline on result cards.
 * Titolare can jump to the Listini Manager (/listini) to upload/delete lists.
 */
export default function ListinoSearchPage() {
  const navigate = useNavigate();
  const { isTitolare } = useAuth();
  const [params] = useSearchParams();
  const inputRef = useRef(null);
  const [q, setQ] = useState(params.get("q") || "");
  const debouncedQ = useDebouncedValue(q, 200);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const query = debouncedQ.trim();
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/products/cache/search", { params: { q: query, limit: 50 } });
      setResults(Array.isArray(data) ? data : []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  const openProduct = (item) => {
    const p = new URLSearchParams();
    if (item.name) p.set("name", item.name);
    if (item.barcode) p.set("barcode", item.barcode);
    if (item.article_code) p.set("article_code", item.article_code);
    if (item.price != null) p.set("price", String(item.price));
    if (item.cost != null) p.set("cost", String(item.cost));
    navigate(`/product/new?${p.toString()}`);
  };

  return (
    <div className="mobile-frame" data-testid="listino-search-page">
      <div className="tf-search-header">
        <button
          data-testid="listino-search-back"
          onClick={() => navigate(-1)}
          className="tf-icon-btn ghost"
          aria-label="Indietro"
        >
          <IoArrowBack size={22} />
        </button>
        <div className="tf-search-input-wrap">
          <IoSearchOutline size={18} className="tf-search-icon" />
          <input
            ref={inputRef}
            data-testid="listino-search-input"
            className="tf-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca nel listino"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {q && (
            <button
              type="button"
              data-testid="listino-search-clear"
              className="tf-icon-btn ghost"
              onClick={() => { setQ(""); inputRef.current?.focus(); }}
              aria-label="Cancella"
              style={{ padding: 6 }}
            >
              <IoClose size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="tf-content" data-testid="listino-search-content" style={{ paddingTop: 4 }}>
        {isTitolare && (
          <button
            type="button"
            data-testid="listino-open-manager-btn"
            className="tf-btn-ghost"
            onClick={() => navigate("/listini")}
            style={{ width: "100%", justifyContent: "center", marginBottom: 12 }}
          >
            <IoLibraryOutline size={18} />
            Gestisci listini Conad
          </button>
        )}

        {loading && <div className="tf-muted" data-testid="listino-loading">Cerco…</div>}
        {!loading && debouncedQ.trim().length >= 2 && results.length === 0 && (
          <div className="tf-empty" data-testid="listino-empty">
            <IoSearchOutline size={44} />
            <div>Nessun risultato per &ldquo;{debouncedQ}&rdquo;</div>
          </div>
        )}
        {!loading && debouncedQ.trim().length < 2 && (
          <div className="tf-empty" data-testid="listino-empty-initial">
            <IoSearchOutline size={44} />
            <div>Digita per cercare nel listino Conad</div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
              31.927 prodotti · nome, EAN, codice articolo
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {results.map((r) => (
            <button
              key={`${r.article_code || r.barcode}`}
              type="button"
              data-testid={`listino-result-${r.article_code || r.barcode}`}
              className="tf-search-result tf-search-result-neutral"
              onClick={() => openProduct(r)}
            >
              <div className="tf-search-result-title">{r.name || "—"}</div>
              <div className="tf-search-result-sub">
                {r.article_code && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 10 }}>
                    <IoCubeOutline size={12} /> {r.article_code}
                  </span>
                )}
                {r.barcode && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 10 }}>
                    <IoBarcodeOutline size={12} /> {r.barcode}
                  </span>
                )}
                {r.price != null && <span style={{ marginRight: 8 }}>€ {Number(r.price).toFixed(2)}</span>}
                {r.cost != null && <span style={{ color: "var(--tf-muted)" }}>costo € {Number(r.cost).toFixed(2)}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
